import React, { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';
import { playSound, SFX_KEYS } from '../game/systems/SoundManager';

// Inject shimmer animation styles
const injectShimmerStyles = (): void => {
  const styleId = 'shipyard-shimmer-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes shipyardShimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

// Skeleton loader component
interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
}

const Skeleton: React.FC<SkeletonProps> = ({ width = '100%', height = 16, style }) => (
  <div style={{
    width,
    height,
    background: 'linear-gradient(90deg, #1f2937 25%, #374151 50%, #1f2937 75%)',
    backgroundSize: '200% 100%',
    animation: 'shipyardShimmer 1.5s ease-in-out infinite',
    ...style,
  }} />
);

// Shipyard loading skeleton
const ShipyardSkeleton: React.FC = () => (
  <>
    {/* Status bar skeleton */}
    <div className="shipyard-status-bar" style={{ opacity: 0.7 }}>
      <Skeleton width={120} height={16} />
      <div className="status-bars">
        <div className="mini-bar-group">
          <Skeleton width={30} height={12} />
          <div className="mini-bar-container">
            <Skeleton width="60%" height={8} />
          </div>
          <Skeleton width={50} height={12} />
        </div>
        <div className="mini-bar-group">
          <Skeleton width={30} height={12} />
          <div className="mini-bar-container">
            <Skeleton width="80%" height={8} />
          </div>
          <Skeleton width={50} height={12} />
        </div>
      </div>
    </div>

    {/* Columns skeleton */}
    <div className="shipyard-columns">
      <div className="shipyard-column">
        <h3>Repairs</h3>
        <div className="repair-options">
          {[1, 2, 3].map(i => (
            <div key={i} className="repair-btn" style={{ opacity: 0.5, cursor: 'default' }}>
              <Skeleton width={40} height={14} />
              <Skeleton width={30} height={12} />
              <Skeleton width={50} height={14} />
            </div>
          ))}
        </div>
      </div>
      <div className="shipyard-column">
        <h3>Upgrades</h3>
        <div className="upgrade-option" style={{ opacity: 0.5 }}>
          <Skeleton width="70%" height={16} style={{ marginBottom: 8 }} />
          <Skeleton width="100%" height={12} style={{ marginBottom: 4 }} />
          <Skeleton width="80%" height={12} style={{ marginBottom: 12 }} />
          <Skeleton width={100} height={32} />
        </div>
      </div>
    </div>
  </>
);

// ============ INTERFACES ============

interface ShipyardData {
  shipType: string;
  hullHp: number;
  maxHullHp: number;
  sailHp: number;
  maxSailHp: number;
  pieces: number;
  level: number;
  repairs: {
    hull: { cost: number; hpRestored: number };
    sail: { cost: number; hpRestored: number };
    full: { cost: number; hullRestored: number; sailRestored: number };
  };
  upgrade: {
    id: string;
    name: string;
    description: string;
    targetShipType: string;
    cost: number;
    levelRequirement: number;
    canAfford: boolean;
    meetsLevel: boolean;
  } | null;
  insurance: {
    hasInsurance: boolean;
    cost: number;
    canAfford: boolean;
    infamy: number;
  };
}

interface ShipyardPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  pieces: number;
  onPiecesChange?: (amount: number) => void;
  shipyardName: string;
}

// ============ COMPONENT ============

export const ShipyardPanel: React.FC<ShipyardPanelProps> = ({ 
  socket, 
  isOpen, 
  onClose, 
  pieces, 
  onPiecesChange,
  shipyardName 
}) => {
  const [shipyardData, setShipyardData] = useState<ShipyardData | null>(null);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { handleHeaderMouseDown, style: dragStyle, panelRef } = useDraggablePanel(150, 150, {
    storageKey: 'panel:shipyard',
    clampToViewport: true,
  });

  // Inject shimmer styles on mount
  useEffect(() => {
    injectShimmerStyles();
  }, []);

  useEffect(() => {
    if (!socket || !isOpen) return;

    // Request shipyard data
    setIsLoading(true);
    socket.emit('shipyard:open', { locationId: shipyardName });

    // Listen for shipyard data
    const handleShipyardData = (data: ShipyardData): void => {
      setShipyardData(data);
      setIsLoading(false);
    };

    const handleRepairResult = (data: { 
      success: boolean; 
      type: string; 
      cost: number; 
      newHullHp: number; 
      newSailHp: number; 
      newPieces?: number;
      newBerries?: number;
    }): void => {
      if (data.success) {
        setMessage(`Repaired ${data.type} for ₱${data.cost.toLocaleString()}`);
        setMessageType('success');
        playSound(SFX_KEYS.SHIP_REPAIR);
        const nextPieces = data.newPieces ?? data.newBerries;
        if (typeof nextPieces === 'number') {
          onPiecesChange?.(nextPieces);
        }
        // Refresh shipyard data
        socket.emit('shipyard:open', { locationId: shipyardName });
      }
    };

    const handleUpgradeResult = (data: { 
      success: boolean; 
      newShipType: string; 
      cost: number; 
      newPieces?: number;
      newBerries?: number;
    }): void => {
      if (data.success) {
        setMessage(`Upgraded to ${data.newShipType}!`);
        setMessageType('success');
        const nextPieces = data.newPieces ?? data.newBerries;
        if (typeof nextPieces === 'number') {
          onPiecesChange?.(nextPieces);
        }
        // Refresh shipyard data
        socket.emit('shipyard:open', { locationId: shipyardName });
      }
    };

    const handleInsuranceResult = (data: {
      success: boolean;
      cost: number;
      newPieces: number;
      hasInsurance: boolean;
    }): void => {
      if (data.success) {
        setMessage(`Insurance purchased for ₱${data.cost.toLocaleString()}!`);
        setMessageType('success');
        onPiecesChange?.(data.newPieces);
        // Refresh shipyard data
        socket.emit('shipyard:open', { locationId: shipyardName });
      }
    };

    const handleError = (data: { message: string }): void => {
      setMessage(data.message);
      setMessageType('error');
    };

    socket.on('shipyard:data', handleShipyardData);
    socket.on('shipyard:repair:result', handleRepairResult);
    socket.on('shipyard:upgrade:result', handleUpgradeResult);
    socket.on('shipyard:insurance:result', handleInsuranceResult);
    socket.on('shipyard:error', handleError);

    return () => {
      socket.off('shipyard:data', handleShipyardData);
      socket.off('shipyard:repair:result', handleRepairResult);
      socket.off('shipyard:upgrade:result', handleUpgradeResult);
      socket.off('shipyard:insurance:result', handleInsuranceResult);
      socket.off('shipyard:error', handleError);
      socket.emit('shipyard:close');
    };
  }, [socket, isOpen, shipyardName, onPiecesChange]);

  // Clear message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (!isOpen) return null;

  const handleRepair = (type: 'hull' | 'sail' | 'full'): void => {
    socket?.emit('shipyard:repair', { type });
  };

  const handleUpgrade = (upgradeId: string): void => {
    socket?.emit('shipyard:upgrade', { upgradeId });
  };

  const handleBuyInsurance = (): void => {
    socket?.emit('shipyard:insurance:buy');
  };

  const hullPercent = shipyardData ? (shipyardData.hullHp / shipyardData.maxHullHp) * 100 : 100;
  const sailPercent = shipyardData ? (shipyardData.sailHp / shipyardData.maxSailHp) * 100 : 100;

  // Helper to get repair button status text
  const getRepairStatus = (cost: number, hpRestored: number): string | null => {
    if (cost === 0 || hpRestored === 0) return 'Full HP';
    if (pieces < cost) return 'Not enough ₱';
    return null;
  };

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        ...dragStyle,
        zIndex: 1000,
      }}
    >
      <div className="shop-panel">
        {/* Header */}
        <div className="shop-panel-header" onMouseDown={handleHeaderMouseDown}>
          <h2 style={{ color: '#ffa500' }}>{shipyardName}</h2>
          <span style={{ color: '#ffd700', fontSize: '14px', marginLeft: 'auto', marginRight: '16px' }}>₱{pieces.toLocaleString()}</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Message */}
        {message && (
          <div className={`shop-message ${messageType}`}>
            {message}
          </div>
        )}

        {/* Content */}
        <div className="shop-content">
          {isLoading || !shipyardData ? (
            <ShipyardSkeleton />
          ) : (
            <>
              {/* Ship Status - Compact header */}
              <div className="shipyard-status-bar">
                <span><strong>{shipyardData.shipType}</strong> Lv.{shipyardData.level}</span>
                <div className="status-bars">
                  <div className="mini-bar-group">
                    <span>Hull</span>
                    <div className="mini-bar-container">
                      <div className="mini-bar hull" style={{ width: `${hullPercent}%` }} />
                    </div>
                    <span className="mini-bar-text">{shipyardData.hullHp}/{shipyardData.maxHullHp}</span>
                  </div>
                  <div className="mini-bar-group">
                    <span>Sail</span>
                    <div className="mini-bar-container">
                      <div className="mini-bar sail" style={{ width: `${sailPercent}%` }} />
                    </div>
                    <span className="mini-bar-text">{shipyardData.sailHp}/{shipyardData.maxSailHp}</span>
                  </div>
                </div>
              </div>

              {/* Two column layout for Repairs and Upgrades */}
              <div className="shipyard-columns">
                {/* Repairs Column */}
                <div className="shipyard-column">
                  <h3>Repairs</h3>
                  <div className="repair-options">
                    <button
                      className="repair-btn"
                      onClick={() => handleRepair('hull')}
                      disabled={shipyardData.repairs.hull.cost === 0 || pieces < shipyardData.repairs.hull.cost}
                      title={getRepairStatus(shipyardData.repairs.hull.cost, shipyardData.repairs.hull.hpRestored) || 'Repair hull damage'}
                    >
                      <span className="repair-name">Hull</span>
                      <span className="repair-info">
                        {getRepairStatus(shipyardData.repairs.hull.cost, shipyardData.repairs.hull.hpRestored) || `+${shipyardData.repairs.hull.hpRestored}`}
                      </span>
                      <span className="repair-cost">₱{shipyardData.repairs.hull.cost.toLocaleString()}</span>
                    </button>

                    <button
                      className="repair-btn"
                      onClick={() => handleRepair('sail')}
                      disabled={shipyardData.repairs.sail.cost === 0 || pieces < shipyardData.repairs.sail.cost}
                      title={getRepairStatus(shipyardData.repairs.sail.cost, shipyardData.repairs.sail.hpRestored) || 'Repair sail damage'}
                    >
                      <span className="repair-name">Sail</span>
                      <span className="repair-info">
                        {getRepairStatus(shipyardData.repairs.sail.cost, shipyardData.repairs.sail.hpRestored) || `+${shipyardData.repairs.sail.hpRestored}`}
                      </span>
                      <span className="repair-cost">₱{shipyardData.repairs.sail.cost.toLocaleString()}</span>
                    </button>

                    <button
                      className="repair-btn full"
                      onClick={() => handleRepair('full')}
                      disabled={shipyardData.repairs.full.cost === 0 || pieces < shipyardData.repairs.full.cost}
                      title={getRepairStatus(shipyardData.repairs.full.cost, shipyardData.repairs.full.hullRestored + shipyardData.repairs.full.sailRestored) || 'Full repair'}
                    >
                      <span className="repair-name">Full</span>
                      <span className="repair-info">
                        {getRepairStatus(shipyardData.repairs.full.cost, shipyardData.repairs.full.hullRestored + shipyardData.repairs.full.sailRestored) || `+${shipyardData.repairs.full.hullRestored}H +${shipyardData.repairs.full.sailRestored}S`}
                      </span>
                      <span className="repair-cost">₱{shipyardData.repairs.full.cost.toLocaleString()}</span>
                    </button>
                  </div>
                </div>

                {/* Upgrades Column */}
                <div className="shipyard-column">
                  <h3>Upgrades</h3>
                  {shipyardData.upgrade ? (
                    <div className="upgrade-option">
                      <div className="upgrade-header">
                        <span className="upgrade-name">{shipyardData.upgrade.name}</span>
                      </div>
                      <p className="upgrade-desc">{shipyardData.upgrade.description}</p>
                      <div className="upgrade-requirements" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className={shipyardData.upgrade.meetsLevel ? 'met' : 'unmet'}>
                          Lv.{shipyardData.upgrade.levelRequirement}
                        </span>
                        <span className="upgrade-cost">₱{shipyardData.upgrade.cost.toLocaleString()}</span>
                      </div>
                      <button
                        className="upgrade-btn"
                        onClick={() => handleUpgrade(shipyardData.upgrade!.id)}
                        disabled={!shipyardData.upgrade.canAfford || !shipyardData.upgrade.meetsLevel}
                      >
                        {!shipyardData.upgrade.meetsLevel 
                          ? `Requires Lv.${shipyardData.upgrade.levelRequirement}`
                          : !shipyardData.upgrade.canAfford
                            ? 'Not Enough ₱'
                            : `Upgrade`
                        }
                      </button>
                    </div>
                  ) : (
                    <div className="no-upgrade">
                      <p>Max tier reached</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Insurance Section */}
              <div className="shipyard-insurance">
                <h3>Ship Insurance</h3>
                <div className="insurance-content">
                  {shipyardData.insurance.hasInsurance ? (
                    <div className="insurance-active">
                      <div className="insurance-status">
                        <span className="insurance-badge">INSURED</span>
                      </div>
                      <p className="insurance-desc">
                        Your ship is protected. On death, you keep 50% of your pieces and items.
                      </p>
                    </div>
                  ) : (
                    <div className="insurance-purchase">
                      <p className="insurance-desc">
                        Protect your cargo! Insurance saves 50% of your pieces and items on death.
                        Consumed on use.
                      </p>
                      <div className="insurance-pricing">
                        <span className="insurance-cost">₱{shipyardData.insurance.cost.toLocaleString()}</span>
                        {shipyardData.insurance.infamy > 0 && (
                          <span className="insurance-infamy">
                            (Base ₱500,000 + ₱{(shipyardData.insurance.infamy * 1000).toLocaleString()} infamy)
                          </span>
                        )}
                      </div>
                      <button
                        className="insurance-btn"
                        onClick={handleBuyInsurance}
                        disabled={!shipyardData.insurance.canAfford}
                      >
                        {shipyardData.insurance.canAfford ? 'Purchase Insurance' : 'Not Enough ₱'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
